import { useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArchiveRestore,
  ArrowLeft,
  ArrowRightLeft,
  Ban,
  Building2,
  CreditCard,
  Globe,
  PauseCircle,
  PlayCircle,
  Radio,
  Server,
  Trash2,
  TriangleAlert,
  Users,
} from 'lucide-react';
import {
  ORG_REFUSED_FIELDS,
  type OrgAttachment,
  type OrgDetail,
  type SlugChangeResult,
  type UpdateOrgRequest,
} from '@simpleacs/shared';
import { api } from '../lib/api';
import { formatTimestamp } from '../lib/format';
import { useSession } from '../lib/session';
import { useTasks } from '../lib/tasks';
import { Button, Card, ErrorNotice, Field, Input, Pill, Spinner, StatCard } from '../components/ui';
import { slugify } from './Organizations';

type OrgDetailResponse = OrgDetail & { slugChange?: SlugChangeResult };

/** "2 OLTs and 1 ONU" — the same phrasing the API refuses with. */
function listAttachments(items: OrgAttachment[]): string {
  const parts = items.map((a) => `${a.count} ${a.label}${a.count === 1 ? '' : 's'}`);
  if (parts.length <= 1) return parts[0] ?? 'nothing';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * The org's accounting records, phrased for a warning.
 *
 * Invoices and payments stopped refusing a delete on 2026-08-25 (the operator's
 * call: equipment only). They are still destroyed by one, so the warning now
 * has to do the work the refusal used to. Read from the org's own count fields
 * rather than by matching label strings inside `erases`, so renaming a label
 * on the API side can never quietly drop this notice.
 */
function financialRecords(org: OrgDetail): string | null {
  const parts: string[] = [];
  if (org.invoiceCount > 0) {
    parts.push(`${org.invoiceCount} invoice${org.invoiceCount === 1 ? '' : 's'}`);
  }
  if (org.paymentCount > 0) {
    parts.push(`${org.paymentCount} recorded payment${org.paymentCount === 1 ? '' : 's'}`);
  }
  return parts.length > 0 ? parts.join(' and ') : null;
}

/**
 * The part of the cascade the counted list does not name.
 *
 * Thirty-one tables carry a foreign key to `organizations` and `erases` counts
 * twenty-one of them. The rest are per-ONU history and per-run state that the
 * equipment blockers already stand in front of, so counting each with its own
 * `count(*)` on every page load would buy precision nobody reads — but leaving
 * the confirmation to imply the list is exhaustive is the thing this dialog
 * exists not to do. The audit trail is the one worth stating explicitly: it is
 * NOT deleted, it is disowned, which is a different and easily-missed answer.
 */
function UncountedCascade() {
  return (
    <p className="text-xs text-faint">
      Also erased, uncounted: sweep and job history, drift findings, ONU event history, SmartOLT
      import records and any scheduled auto-authorise window. The audit trail is not deleted — its
      entries survive with the organization column blanked, so who-did-what stays readable and stops
      naming a tenant that no longer exists.
    </p>
  );
}

/**
 * `Sept 2026` — the month a UTC month-start falls in, named in UTC.
 *
 * Every billing boundary on this platform is a UTC month start, and rendering
 * one in the reader's own zone is how "September" becomes "31 August" for
 * anybody west of Greenwich. Same rule as `monthDate` on the billing pages.
 */
function monthLabel(ts: number): string {
  return new Date(ts).toLocaleDateString('en-GB', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** `2026-09`, what `<input type="month">` speaks, from a UTC month start. */
function monthInputValue(ts: number | null): string {
  if (ts === null) return '';
  const d = new Date(ts);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** The UTC month start a `<input type="month">` value names, or null for an empty box. */
function monthInputToUtc(value: string): number | null {
  const m = /^(\d{4})-(\d{2})$/.exec(value);
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, 1);
}

function Row({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b border-line px-5 py-3 last:border-b-0">
      <div>
        <span className="text-sm text-muted">{label}</span>
        {hint && <p className="mt-0.5 max-w-md text-xs text-faint">{hint}</p>}
      </div>
      <span className="text-sm text-ink">{value}</span>
    </div>
  );
}

export function OrganizationDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, session } = useSession();
  const isSuperadmin = session?.user.role === 'superadmin';
  const platform = can('org.manage');

  const [editing, setEditing] = useState(false);
  const [suspending, setSuspending] = useState(false);
  const [movingBillingSince, setMovingBillingSince] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [receipt, setReceipt] = useState<SlugChangeResult | null>(null);

  const orgQuery = useQuery({
    queryKey: ['org', id],
    queryFn: () => api.get<OrgDetail>(`/orgs/${encodeURIComponent(id)}`),
  });

  /**
   * One PATCH for every small change on this header — status, and now billing.
   *
   * Typed as the request the API actually accepts rather than as the one field
   * it started with: narrowing it to `{status}` meant the next field to arrive
   * was a type error at the call site rather than a working control.
   */
  const patchOrg = useMutation({
    mutationFn: (input: UpdateOrgRequest) =>
      api.patch<OrgDetailResponse>(`/orgs/${encodeURIComponent(id)}`, input),
    onSuccess: async (_result, input) => {
      await queryClient.invalidateQueries({ queryKey: ['org', id] });
      await queryClient.invalidateQueries({ queryKey: ['orgs'] });
      /**
       * The VLAN lists carry this tenant's VLAN 1 answer alongside the rows,
       * so turning it on or off makes every one of them stale — on every
       * chassis at once, which is the whole point of an org-level switch.
       *
       * Matched by predicate rather than by invalidating `['olt']` wholesale:
       * that prefix also covers the cards, PON ports and running config of
       * every OLT page open in this tab, and re-reading a chassis inventory is
       * not a free consequence of ticking a checkbox. The three shapes below
       * are every key that fetches a VLAN list — the OLT page's, the dialogs'
       * (`olt-vlans`), and the per-ONU candidate list.
       */
      if (input.vlan1Selectable === undefined) return;
      await queryClient.invalidateQueries({
        predicate: ({ queryKey: key }) =>
          (key[0] === 'olt' && key[2] === 'vlans') ||
          key[0] === 'olt-vlans' ||
          (key[0] === 'onu-config' && key[2] === 'available-vlans'),
      });
    },
  });

  /**
   * Calling off a scheduled deletion. Its own mutation rather than a PATCH,
   * because it is one act with two halves — the date and the suspension were
   * set together and are lifted together, and a status PATCH would clear only
   * one of them and leave an active org still counting down.
   */
  const unarchive = useMutation({
    mutationFn: () =>
      api.post<OrgDetail>(`/orgs/${encodeURIComponent(id)}/migration/unarchive`, { note: null }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['org', id] });
      await queryClient.invalidateQueries({ queryKey: ['orgs'] });
    },
  });

  if (orgQuery.isLoading) {
    return (
      <div className="flex justify-center p-16">
        <Spinner className="size-6" />
      </div>
    );
  }
  if (orgQuery.error) return <ErrorNotice error={orgQuery.error} />;
  const org = orgQuery.data;
  if (!org) return null;

  const suspended = org.status === 'suspended';
  /**
   * Two different questions, and they used to be one.
   *
   * `org.manage` is platform-only — no org role can hold it, by construction
   * (`PLATFORM_CAPABILITIES`) — so gating the whole action bar on it meant an
   * org admin could not record their own company's accounts clerk. The
   * platform buttons below still ask for it; `canEditProfile` asks whether
   * this caller may edit THIS organization at all, which for a tenant means
   * its own and only its contact details. The API decides the same way and is
   * the enforcement; this only decides whether a button is worth showing.
   *
   * Compared against the LOADED org's id rather than the `:id` in the URL,
   * which is now a number as often as an id (`/organizations/23`) — against
   * the URL this is false on exactly the address an operator actually types.
   */
  const editsOwnOrg = !platform && session?.user.orgId === org.id && can('settings.manage');
  const canEditProfile = platform || editsOwnOrg;

  return (
    <>
      <button
        onClick={() => navigate('/organizations')}
        className="mb-4 inline-flex items-center gap-2 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" />
        Back to organizations
      </button>

      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl font-semibold text-ink">{org.name}</h1>
            <Pill state tone={suspended ? 'warn' : 'success'} dot>
              {suspended ? 'suspended' : 'active'}
            </Pill>
            <span className="font-mono text-xs text-muted">{org.slug}</span>
          </div>
          <p className="mt-1 text-sm text-muted">
            Created {formatTimestamp(org.createdAt)}
            {org.statusChangedAt
              ? ` · ${suspended ? 'suspended' : 'reactivated'} ${formatTimestamp(org.statusChangedAt)}`
              : ''}
          </p>
          {org.statusNote && <p className="mt-1 text-sm text-warn">“{org.statusNote}”</p>}
          {/*
            The floor the arrears computation charges from, beside the switch
            that decides whether it charges at all. Platform-only: the API
            refuses everyone else, and a tenant shown the month its own bill
            started from with an Edit beside it would be invited into a 403.
          */}
          {platform && (
            <p className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-muted">
              <span>
                Billing since{' '}
                {org.billingSince === null ? (
                  <span className="text-faint">not set (seeds on first charge)</span>
                ) : (
                  <span className="text-ink">{monthLabel(org.billingSince)}</span>
                )}
              </span>
              {isSuperadmin && (
                <button
                  type="button"
                  className="text-xs text-link hover:underline"
                  onClick={() => setMovingBillingSince(true)}
                >
                  Edit
                </button>
              )}
            </p>
          )}
        </div>

        {canEditProfile && (
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setEditing(true)}>Edit</Button>
            {/*
              Everything past Edit is the platform acting ON this tenant —
              suspending it, un-billing it, emptying it, deleting it. A tenant
              editing its own contact details gets the Edit button and nothing
              else, because the API refuses every one of these to them.
            */}
            {platform && (
              <>
                <Button
                  variant={suspended ? 'success' : 'primary'}
                  icon={
                    suspended ? (
                      <PlayCircle className="size-4" />
                    ) : (
                      <PauseCircle className="size-4" />
                    )
                  }
                  loading={patchOrg.isPending}
                  onClick={() =>
                    // Reactivating needs no explanation; suspending does, and
                    // the reason belongs on the org rather than only in the
                    // audit log.
                    suspended
                      ? patchOrg.mutate({ status: 'active', statusNote: null })
                      : setSuspending(true)
                  }
                >
                  {suspended ? 'Reactivate' : 'Suspend'}
                </Button>
                {/*
                  Billing, separately from suspension, because they are
                  different questions. Suspending stops an org USING the
                  platform and says nothing about money; this stops the
                  platform CHARGING them and says nothing about access.
                  Expressing "we do not bill these people" through the other
                  switch would take their service away.
                */}
                <Button
                  variant={org.billingEnabled === false ? 'primary' : 'ghost'}
                  icon={
                    org.billingEnabled === false ? (
                      <CreditCard className="size-4" />
                    ) : (
                      <Ban className="size-4" />
                    )
                  }
                  loading={patchOrg.isPending}
                  onClick={() => patchOrg.mutate({ billingEnabled: org.billingEnabled === false })}
                >
                  {org.billingEnabled === false ? 'Start billing' : 'Stop billing'}
                </Button>
                {/*
                  Beside Delete, and deliberately so: "hand this tenant's fleet
                  to somebody else" is the alternative an operator should see at
                  the moment they are reaching for the destructive button. It is
                  the only way to keep an ONU's history, its backups and its
                  optical trend while changing who owns it.
                */}
                {isSuperadmin && (
                  <Button
                    icon={<ArrowRightLeft className="size-4" />}
                    onClick={() => navigate(`/organizations/${org.number ?? org.id}/migration`)}
                  >
                    Move equipment
                  </Button>
                )}
                {isSuperadmin && (
                  <Button
                    variant="danger"
                    icon={<Trash2 className="size-4" />}
                    onClick={() => setConfirmingDelete(true)}
                  >
                    Delete
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {patchOrg.error && (
        <div className="mb-4">
          <ErrorNotice error={patchOrg.error} />
        </div>
      )}

      {/*
        Archived is stricter than suspended: suspended is a wall, archived is a
        wall with a date on it. The date and the way out are shown together —
        an archive whose undo an operator has to go looking for is a delete with
        a delay.
      */}
      {org.purgeAfter && (
        <div className="mb-4 rounded-lg border border-danger/30 bg-danger-soft px-4 py-3">
          <p className="flex items-center gap-2 text-sm font-medium text-danger">
            <TriangleAlert className="size-4" />
            Archived — scheduled for deletion {formatTimestamp(org.purgeAfter)}.
          </p>
          <p className="mt-1 text-xs text-muted">
            When that day comes, a background job deletes this organization and everything it still
            holds. Until then nothing has been destroyed and this is fully reversible.
          </p>
          {isSuperadmin && (
            <Button
              className="mt-2"
              size="sm"
              variant="success"
              icon={<ArchiveRestore className="size-4" />}
              loading={unarchive.isPending}
              onClick={() => unarchive.mutate()}
            >
              Call off the deletion
            </Button>
          )}
          {unarchive.error && (
            <div className="mt-2">
              <ErrorNotice error={unarchive.error} />
            </div>
          )}
        </div>
      )}

      {receipt && (
        <div className="mb-4 rounded-lg border border-warn/30 bg-warn-soft px-4 py-3 text-sm text-ink">
          <p className="font-medium">
            Slug moved from {receipt.from} to {receipt.to}.
          </p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs text-muted">
            {receipt.subdomainFrom && receipt.subdomainTo && (
              <li>
                {receipt.subdomainFrom} no longer resolves — the org is now at {receipt.subdomainTo}
                . Anyone signed in through the old hostname has to sign in again.
              </li>
            )}
            <li>
              {receipt.acsDevicesRetagging} device
              {receipt.acsDevicesRetagging === 1 ? '' : 's'} being retagged at the ACS in the
              background.
            </li>
            {receipt.informUsername && (
              <li>
                TR-069 inform username stays {receipt.informUsername} — it is written into every ONU
                already on a chassis.
              </li>
            )}
          </ul>
          <button
            className="mt-2 text-xs text-link hover:underline"
            onClick={() => setReceipt(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="OLTs" value={org.oltCount} icon={<Server className="size-4" />} />
        <StatCard label="ONUs" value={org.onuCount} icon={<Radio className="size-4" />} />
        <StatCard
          label="TR-069 devices"
          value={org.acsDeviceCount}
          icon={<Globe className="size-4" />}
          hint="Claimed at the shared GenieACS"
        />
        <StatCard label="Users" value={org.userCount ?? 0} icon={<Users className="size-4" />} />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card title="Where this organization answers">
          <Row
            label="Platform subdomain"
            value={
              org.platformSubdomain ? (
                <span className="font-mono text-xs">{org.platformSubdomain}</span>
              ) : (
                <span className="text-faint">no base domain configured</span>
              )
            }
            hint="Derived from the slug. Changing the slug moves it."
          />
          <Row
            label="White-label domain"
            value={
              org.customDomain ? (
                <span className="font-mono text-xs">{org.customDomain}</span>
              ) : (
                <span className="text-faint">none</span>
              )
            }
            hint="The org's own hostname, verified and served by nginx."
          />
          <Row
            label="ACS device tag"
            value={<span className="font-mono text-xs">{org.acsOrgTag}</span>}
            hint="What binds a device in the shared GenieACS to this tenant."
          />
          <Row
            label="TR-069 inform username"
            value={
              org.informUsername ? (
                <span className="font-mono text-xs">{org.informUsername}</span>
              ) : (
                <span className="text-faint">no local profile</span>
              )
            }
            hint="Written into every provisioned ONU. It does not follow a slug change."
          />
        </Card>

        <Card title="What this tenant holds">
          <Row label="Gateways" value={org.siteCount ?? 0} />
          <Row label="API keys" value={org.apiKeyCount} />
          <Row label="Zones / splitters" value={`${org.zoneCount} / ${org.splitterCount}`} />
          <Row
            label="Speed / TR-069 profiles"
            value={`${org.speedProfileCount} / ${org.tr069ProfileCount}`}
          />
          <Row
            label="Billing"
            value={
              org.paidThrough ? `paid through ${formatTimestamp(org.paidThrough)}` : 'never paid'
            }
            hint={`${org.invoiceCount} invoice${org.invoiceCount === 1 ? '' : 's'}, ${org.paymentCount} payment${org.paymentCount === 1 ? '' : 's'} recorded.`}
          />
        </Card>

        {/*
          The account number and the contact sit together because they are the
          two things somebody looking at this page from a support call actually
          needs: what the customer pays against, and who to ring. One of them
          can never be edited and the other is ordinary text — the hints say
          which is which, in the API's own words.
        */}
        <Card title="Account and contact">
          <Row
            label="Account number"
            value={
              org.accountNumber === null ? (
                <span className="text-faint">not issued</span>
              ) : (
                <span className="font-mono text-xs">{org.accountNumber}</span>
              )
            }
            hint="What a customer's M-PESA payment is made against, and what is printed on every invoice. Issued once; it cannot be changed."
          />
          <Row
            label="Contact"
            value={org.contactName ?? <span className="text-faint">nobody recorded</span>}
          />
          <Row
            label="Email"
            value={
              org.contactEmail ? (
                <a className="text-link hover:underline" href={`mailto:${org.contactEmail}`}>
                  {org.contactEmail}
                </a>
              ) : (
                <span className="text-faint">none</span>
              )
            }
          />
          <Row
            label="Phone"
            value={
              org.contactPhone ? (
                <a
                  className="font-mono text-xs text-link hover:underline"
                  href={`tel:${org.contactPhone}`}
                >
                  {org.contactPhone}
                </a>
              ) : (
                <span className="text-faint">none</span>
              )
            }
            hint="Nothing is sent here automatically — every message on this platform still asks who it goes to."
          />
          <Row label="Last edited" value={formatTimestamp(org.updatedAt)} />
        </Card>
      </div>

      {/*
        The platform's private note, and the API is what keeps it private:
        `loadOrgDetail` omits the KEY entirely for a reader without
        `org.manage`, so `platformNote === undefined` here is the absence of
        permission and not the absence of a note. Rendered inside the same
        `platform` guard anyway — a card that only ever appears for one kind of
        reader should not depend on the shape of a response to stay hidden.
      */}
      {platform && (
        <Card title="Platform note" className="mt-5">
          <div className="px-5 py-4">
            {org.platformNote ? (
              <p className="whitespace-pre-wrap text-sm text-ink">{org.platformNote}</p>
            ) : (
              <p className="text-sm text-faint">Nothing recorded.</p>
            )}
            <p className="mt-2 text-xs text-faint">
              Yours, about this tenant — the reseller they came through, why they are
              billing-exempt, what the suspension six months ago was really about. The organization
              never sees it: it is not in the copy of this page its own admins read, it is not in
              what they get back from a save, and the audit trail records that it changed without
              recording what it says. The suspension reason above is the opposite — that one is
              shown to them on purpose.
            </p>
          </div>
        </Card>
      )}

      {can('org.manage') && (
        <Card title="Provisioning defaults" className="mt-5">
          <div className="space-y-3 p-5 text-sm">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="max-w-xl">
                <p className="font-medium text-ink">
                  Offer VLAN 1 as a subscriber VLAN
                  <Pill state tone={org.vlan1Selectable ? 'warn' : 'info'} className="ml-2">
                    {org.vlan1Selectable ? 'offered' : 'hidden'}
                  </Pill>
                </p>
                <p className="mt-1 text-xs text-muted">
                  VLAN 1 is every switch&rsquo;s untagged default and carries whatever nobody
                  classified, so a subscriber on it is indistinguishable from one never configured.
                  With this off it does not appear in the authorise wizard, the WAN dialogs, the
                  bulk form or an ONU&rsquo;s attachable VLANs.
                </p>
                {/*
                  Said on the control itself, not only in a changelog. The
                  reason this switch is safe to leave off is that it hides a
                  choice rather than withdrawing a service, and an operator
                  deciding whether to flip it is exactly who needs to know that.
                */}
                <p className="mt-1 text-xs text-muted">
                  It never takes an ONU off the air. Subscribers already on VLAN 1 keep working,
                  their WAN stays editable, their config still reads and applies, and every list
                  and report shows their real VLAN. A single chassis can still offer VLAN 1 on its
                  own VLANs tab whatever this says.
                </p>
              </div>
              <Button
                variant={org.vlan1Selectable ? 'ghost' : 'primary'}
                loading={patchOrg.isPending}
                onClick={() => patchOrg.mutate({ vlan1Selectable: !org.vlan1Selectable })}
              >
                {org.vlan1Selectable ? 'Stop offering VLAN 1' : 'Offer VLAN 1'}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {isSuperadmin && (
        <Card title="Removing this organization" className="mt-5">
          <div className="space-y-3 p-5 text-sm">
            <p className="text-muted">
              <strong className="text-ink">Suspend is the usual answer.</strong> It locks the
              org&rsquo;s people and its API keys out of the platform while every row it owns —
              OLTs, ONUs, topology, billing history — stays exactly where it is, and the sweeps and
              the chassis carry on untouched. It is undone in one click. Deleting is for an
              organization that never carried anything: a name typed wrong on the day it was
              created.
            </p>
            {org.deletable ? (
              <>
                <p className="text-muted">
                  No equipment is attached, so this organization can be deleted. It would still
                  erase {org.erases.length > 0 ? listAttachments(org.erases) : 'nothing'}.
                </p>
                {financialRecords(org) && (
                  <p className="flex items-start gap-2 text-danger">
                    <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                    <span>
                      Including {financialRecords(org)}. Nothing refuses the delete over those —
                      only equipment does — so this is the only warning you get before the
                      org&rsquo;s billing history stops existing. Suspend keeps it.
                    </span>
                  </p>
                )}
              </>
            ) : (
              <p className="flex items-start gap-2 text-warn">
                <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                <span>
                  Delete is refused: {listAttachments(org.deleteBlockers)} still attached. Move or
                  remove them first, or suspend the organization instead.
                </span>
              </p>
            )}
          </div>
        </Card>
      )}

      {suspending && <SuspendOrgDialog org={org} onClose={() => setSuspending(false)} />}
      {movingBillingSince && (
        <BillingSinceDialog org={org} onClose={() => setMovingBillingSince(false)} />
      )}

      {editing && (
        <EditOrgDialog
          org={org}
          platform={platform}
          onClose={() => setEditing(false)}
          onSaved={(change) => {
            setEditing(false);
            if (change) setReceipt(change);
          }}
        />
      )}

      {confirmingDelete && (
        <DeleteOrgDialog
          org={org}
          onClose={() => setConfirmingDelete(false)}
          onDeleted={() => navigate('/organizations')}
        />
      )}
    </>
  );
}

/**
 * Suspending, with the reason kept.
 *
 * This is the archive path — the one that should be reached for instead of a
 * delete — and an archive nobody can explain six months later gets undone by
 * guess. The note is optional but asked for every time.
 */
function SuspendOrgDialog({ org, onClose }: { org: OrgDetail; onClose: () => void }) {
  const tasks = useTasks();
  const [note, setNote] = useState('');

  /**
   * The dialog closes on Suspend and the write runs in the tasks tray — the
   * platform rule for every modal: the operator has decided, and the outcome
   * (or the refusal) reaches them as a toast wherever they are next.
   */
  const suspend = () => {
    const statusNote = note.trim() || null;
    onClose();
    tasks.start({
      label: `Suspend ${org.name}`,
      run: () =>
        api.patch<OrgDetailResponse>(`/orgs/${encodeURIComponent(org.id)}`, {
          status: 'suspended',
          statusNote,
        }),
      done: () => `${org.name} suspended — its users and keys can no longer sign in.`,
      href: `/organizations/${org.id}`,
      invalidate: [['org', org.id], ['orgs']],
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          suspend();
        }}
        className="relative w-full max-w-lg rounded-xl border border-line bg-surface shadow-2xl"
      >
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">Suspend {org.name}</h2>
          <p className="mt-0.5 text-xs text-muted">
            Its {org.userCount ?? 0} user{(org.userCount ?? 0) === 1 ? '' : 's'} and{' '}
            {org.apiKeyCount} API key{org.apiKeyCount === 1 ? '' : 's'} stop being able to sign in.
            Nothing is deleted, the OLTs keep being swept, and subscribers are not touched.
          </p>
        </header>
        <div className="p-5">
          <Field label="Reason (optional)" hint="Shown on this page for whoever looks next.">
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Contract ended — keeping the records"
              maxLength={400}
            />
          </Field>
        </div>
        <footer className="flex justify-end gap-2 border-t border-line px-5 py-3.5">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            Suspend
          </Button>
        </footer>
      </form>
    </div>
  );
}

/**
 * Move the first month this organization may be charged for.
 *
 * The date seeds itself to the current month the first time the org is
 * assessed under a live rate, so switching billing on never bills a tenant for
 * its whole history by accident — and the seeder then never touches it. This
 * is the one deliberate way to overrule that, in both directions. Same shape
 * as `SuspendOrgDialog`: a mutation the page already owns, closing on success,
 * with the org query (which carries the value) and the billing status (which
 * is computed from it) both refreshed — through the tasks tray, the dialog
 * closing the moment the operator commits.
 */
function BillingSinceDialog({ org, onClose }: { org: OrgDetail; onClose: () => void }) {
  const tasks = useTasks();
  const [month, setMonth] = useState(monthInputValue(org.billingSince));
  const [error, setError] = useState<string | null>(null);

  const move = (billingSince: number | null) => {
    onClose();
    tasks.start({
      label: `Billing start for ${org.name}`,
      run: () =>
        api.put<OrgDetailResponse>(`/orgs/${encodeURIComponent(org.id)}/billing-since`, {
          billingSince,
        }),
      done: () =>
        billingSince === null
          ? `${org.name}'s billing start cleared — the next assessment seeds it.`
          : `${org.name} is billable from ${monthLabel(billingSince)}.`,
      href: `/organizations/${org.id}`,
      invalidate: [['org', org.id], ['orgs'], ['billing', 'status']],
    });
  };

  const parsed = monthInputToUtc(month);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          if (parsed === null) {
            setError('Pick a month, or use Clear to unset the date.');
            return;
          }
          move(parsed);
        }}
        className="relative w-full max-w-lg rounded-xl border border-line bg-surface shadow-2xl"
      >
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">Billing since — {org.name}</h2>
          <p className="mt-0.5 text-xs text-muted">
            The first month this organization can be charged for. Moving it backwards bills history
            deliberately — every month from then on is owed at once, and the arrears wall can go up
            on their web UI today. Moving it forward forgives the months before it.
          </p>
        </header>
        <div className="p-5">
          <Field
            label="First billable month"
            hint={
              org.billingSince === null
                ? 'Not set yet. Left empty, it seeds itself to whichever month the organization is first assessed under a live rate.'
                : `Currently ${monthLabel(org.billingSince)}. Clear it and the next assessment seeds it to that month, not this one.`
            }
          >
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          </Field>
        </div>
        {error && <p className="px-5 pb-1 text-sm font-medium text-danger">{error}</p>}
        <footer className="flex flex-wrap justify-between gap-2 border-t border-line px-5 py-3.5">
          <Button
            type="button"
            variant="ghost"
            disabled={org.billingSince === null}
            onClick={() => {
              setError(null);
              move(null);
            }}
          >
            Clear
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              Save
            </Button>
          </div>
        </footer>
      </form>
    </div>
  );
}

/**
 * The organization editor. One dialog, every editable field.
 *
 * Three rules shape it. The slug is not treated as another text box: it is the
 * org's hostname label and its ACS tag, so the dialog spells out what moves
 * BEFORE the save rather than leaving the operator to find out when a bookmark
 * stops working. Fields that CANNOT be edited are shown here anyway,
 * read-only, with the reason — an account number simply absent from the edit
 * screen reads as an oversight, and the operator's next move is to go hunting
 * for the screen that has it. And what a tenant may not change is not
 * rendered for a tenant at all, because a control whose only outcome is a 403
 * is worse than one that is not there.
 *
 * `platform` is the caller holding `org.manage`. Without it this is an
 * organization editing its own contact details — and NOT its name, which is
 * the "Billed to" line on invoices this platform has already issued and
 * shared. `ORG_OWN_FIELDS` in `@simpleacs/shared` carries that reasoning and
 * the API enforces it; hiding the boxes here is so nobody is invited to type
 * into one that cannot be saved.
 */
function EditOrgDialog({
  org,
  platform,
  onClose,
  onSaved,
}: {
  org: OrgDetail;
  platform: boolean;
  onClose: () => void;
  onSaved: (change: SlugChangeResult | null) => void;
}) {
  const tasks = useTasks();
  const [name, setName] = useState(org.name);
  const [slug, setSlug] = useState(org.slug);
  const [statusNote, setStatusNote] = useState(org.statusNote ?? '');
  const [contactName, setContactName] = useState(org.contactName ?? '');
  const [contactEmail, setContactEmail] = useState(org.contactEmail ?? '');
  const [contactPhone, setContactPhone] = useState(org.contactPhone ?? '');
  const [platformNote, setPlatformNote] = useState(org.platformNote ?? '');

  const movingSlug = platform && slug.trim() !== org.slug;
  const nextSubdomain = org.baseDomain ? `${slug.trim()}.${org.baseDomain}` : null;

  /**
   * Only what actually changed is sent.
   *
   * An absent key means "leave it"; an empty box means "clear it", which is
   * why the comparison is against the stored value rather than against ''.
   * Sending every field on every save would also stamp `updatedAt` and write
   * an audit row naming six fields for a one-word change — and, for a tenant,
   * would put `name` in a body the API is right to refuse.
   */
  const changed = (next: string, current: string | null | undefined): string | null | undefined => {
    const trimmed = next.trim();
    const stored = (current ?? '').trim();
    if (trimmed === stored) return undefined;
    return trimmed === '' ? null : trimmed;
  };

  /**
   * Closes on Save and writes through the tasks tray. `onSaved` still fires
   * on success so the page can show the slug-change receipt — that receipt is
   * the one outcome a toast cannot carry (new subdomain, what moved).
   */
  const save = () => {
      const patch: UpdateOrgRequest = {};
      const contactNameChange = changed(contactName, org.contactName);
      if (contactNameChange !== undefined) patch.contactName = contactNameChange;
      const emailChange = changed(contactEmail, org.contactEmail);
      if (emailChange !== undefined) patch.contactEmail = emailChange;
      const phoneChange = changed(contactPhone, org.contactPhone);
      if (phoneChange !== undefined) patch.contactPhone = phoneChange;
      if (platform) {
        if (name.trim() !== org.name) patch.name = name.trim();
        if (movingSlug) patch.slug = slug.trim();
        const noteChange = changed(statusNote, org.statusNote);
        if (noteChange !== undefined) patch.statusNote = noteChange;
        const platformNoteChange = changed(platformNote, org.platformNote);
        if (platformNoteChange !== undefined) patch.platformNote = platformNoteChange;
      }
      onClose();
      tasks.start<OrgDetailResponse>({
        label: `Save ${org.name}`,
        run: async () => {
          const updated = await api.patch<OrgDetailResponse>(
            `/orgs/${encodeURIComponent(org.id)}`,
            patch,
          );
          onSaved(updated.slugChange ?? null);
          return updated;
        },
        done: (updated) =>
          updated.slugChange
            ? `${updated.name} saved — its subdomain moved, see the receipt on its page.`
            : `${updated.name} saved.`,
        href: `/organizations/${org.id}`,
        invalidate: [['org', org.id], ['orgs'], ['session']],
      });
  };

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    save();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <form
        onSubmit={onSubmit}
        className="relative w-full max-w-lg rounded-xl border border-line bg-surface shadow-2xl"
      >
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">Edit organization</h2>
          {!platform && (
            <p className="mt-0.5 text-xs text-muted">
              Who to ring at {org.name}. The name itself, the slug, the suspension and the billing
              switch belong to the platform — the name because it is the &ldquo;Billed to&rdquo;
              line on invoices already issued to you, which a rename would rewrite. Ask the
              platform for one.
            </p>
          )}
        </header>

        <div className="grid gap-4 p-5">
          {platform && (
            <>
              <Field label="Name">
                <Input value={name} onChange={(e) => setName(e.target.value)} required />
              </Field>
              <Field
                label="Slug"
                hint="Lowercase letters, digits and single dashes. It is a hostname label, not a display name."
              >
                <Input value={slug} onChange={(e) => setSlug(slugify(e.target.value))} required />
              </Field>
            </>
          )}

          {movingSlug && (
            <div className="rounded-lg border border-warn/30 bg-warn-soft px-4 py-3 text-sm">
              <p className="flex items-start gap-2 font-medium text-ink">
                <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warn" />
                Changing the slug moves this organization&rsquo;s address.
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-muted">
                {org.platformSubdomain && nextSubdomain ? (
                  <li>
                    <span className="font-mono">{org.platformSubdomain}</span> stops resolving; the
                    org moves to <span className="font-mono">{nextSubdomain}</span>. Saved links and
                    open sessions on the old hostname break — people will have to sign in again.
                  </li>
                ) : (
                  <li>
                    No platform base domain is configured yet, so no subdomain moves today — but the
                    slug is what the subdomain will be built from.
                  </li>
                )}
                <li>
                  {org.acsDeviceCount} TR-069 device
                  {org.acsDeviceCount === 1 ? '' : 's'} will be retagged at the shared GenieACS from{' '}
                  <span className="font-mono">{org.acsOrgTag}</span> to{' '}
                  <span className="font-mono">org:{slug.trim()}</span>. That runs in the background
                  after the save.
                </li>
                <li>
                  The TR-069 inform username{' '}
                  <span className="font-mono">{org.informUsername ?? 'acs…1'}</span> is
                  <strong className="text-ink"> not </strong>
                  changed — it is written into every ONU already provisioned on a chassis, and
                  rewriting it here would stop them informing.
                </li>
              </ul>
            </div>
          )}

          <div className="grid gap-4 border-t border-line pt-4 sm:grid-cols-2">
            <Field label="Contact name" hint="The person to ask for, not a login on this platform.">
              <Input
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="Jane Wanjiru"
                maxLength={120}
              />
            </Field>
            <Field label="Contact email">
              <Input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="billing@theirisp.com"
                maxLength={200}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field
                label="Contact phone"
                hint="International, with the country code — +254712345678. Stored the way the SMS gateway would dial it, and nothing is sent to it automatically."
              >
                <Input
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="+254712345678"
                  maxLength={40}
                />
              </Field>
            </div>
          </div>

          {platform && (
            <>
              <Field
                label="Suspension reason"
                hint="Shown on this page — to this organization's own admins too — while it is suspended. Clearing it leaves the status alone."
              >
                <Input
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                  placeholder="Contract ended — keeping the records"
                  maxLength={400}
                />
              </Field>
              <Field
                label="Platform note"
                hint="Yours, not theirs. This organization cannot read it, cannot get it back from a save, and the audit trail records that it changed without recording what it says."
              >
                <textarea
                  className="input min-h-[72px]"
                  value={platformNote}
                  onChange={(e) => setPlatformNote(e.target.value)}
                  placeholder="Came through the Nakuru reseller; billing exempt until their own paybill is live."
                  maxLength={2000}
                />
              </Field>
            </>
          )}

          {/*
            Shown, not hidden. Both are things somebody opens this dialog
            looking for, and a field that is simply absent reads as an
            oversight — so each is here, disabled, carrying the API's own
            refusal as its hint. One sentence, one place: ORG_REFUSED_FIELDS.
          */}
          <div className="grid gap-4 border-t border-line pt-4">
            <Field label="Account number" hint={ORG_REFUSED_FIELDS.accountNumber}>
              <Input value={org.accountNumber ?? 'not issued'} disabled readOnly />
            </Field>
            <Field label="White-label domain" hint={ORG_REFUSED_FIELDS.customDomain}>
              <Input value={org.customDomain ?? 'none'} disabled readOnly />
            </Field>
          </div>
        </div>

        <footer className="flex justify-end gap-2 border-t border-line px-5 py-3.5">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            Save
          </Button>
        </footer>
      </form>
    </div>
  );
}

/**
 * Hard delete, behind the org's own name typed back.
 *
 * Not a yes/no button: the row being removed takes a tenant's OLTs, ONUs,
 * users, keys, topology and billing history with it, and a modal button is one
 * mis-click. The dialog leads with suspend and lists exactly what would go.
 */
function DeleteOrgDialog({
  org,
  onClose,
  onDeleted,
}: {
  org: OrgDetail;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const tasks = useTasks();
  const [typed, setTyped] = useState('');

  /**
   * The typed name is the gate and stays in the dialog; the delete itself
   * runs in the tray. `onDeleted` (the navigation away) fires only once the
   * API has actually removed the row — a refusal leaves the operator on the
   * page it refused for, with the toast saying why.
   */
  const remove = () => {
    const confirmName = typed;
    onClose();
    tasks.start({
      label: `Delete ${org.name}`,
      run: async () => {
        const r = await api.delete<{ ok: boolean }>(`/orgs/${encodeURIComponent(org.id)}`, {
          confirmName,
        });
        onDeleted();
        return r;
      },
      done: () => `${org.name} deleted.`,
      href: '/organizations',
      invalidate: [['orgs'], ['session']],
    });
  };

  const matches = typed.trim() === org.name.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          remove();
        }}
        className="relative w-full max-w-lg rounded-xl border border-line bg-surface shadow-2xl"
      >
        <header className="border-b border-line px-5 py-4">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-danger">
            <Trash2 className="size-4" />
            Delete {org.name}
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            This cannot be undone. Suspending keeps everything and is reversible.
          </p>
        </header>

        <div className="grid gap-4 p-5 text-sm">
          {!org.deletable ? (
            <p className="flex items-start gap-2 text-warn">
              <TriangleAlert className="mt-0.5 size-4 shrink-0" />
              <span>
                {listAttachments(org.deleteBlockers)} still attached to this organization. Move or
                remove them first — or suspend it instead, which keeps every record.
              </span>
            </p>
          ) : org.erases.length > 0 ? (
            <div className="space-y-3">
              {/*
                Money leads, above the list, because nothing refuses the delete
                over it any more. Buried as one row among sixteen it would read
                as bookkeeping rather than as the accounting record it is.
              */}
              {financialRecords(org) && (
                <div className="rounded-lg border border-danger/30 bg-danger-soft px-4 py-3">
                  <p className="flex items-start gap-2 text-sm font-medium text-danger">
                    <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                    <span>
                      This destroys {financialRecords(org)} — permanently, with no copy anywhere
                      else.
                    </span>
                  </p>
                  <p className="mt-1 pl-6 text-xs text-muted">
                    Only equipment refuses a delete, so the platform will not stop you here. If the
                    billing history matters, suspend the organization instead — it keeps every
                    invoice and payment and can be undone.
                  </p>
                </div>
              )}
              <div>
                <p className="text-muted">Deleting erases, permanently:</p>
                <ul className="mt-2 grid gap-1 text-xs text-ink sm:grid-cols-2">
                  {org.erases.map((a) => (
                    <li key={a.label} className="flex items-center gap-2">
                      <Building2 className="size-3.5 text-faint" />
                      <span className="tabular-nums">{a.count}</span> {a.label}
                      {a.count === 1 ? '' : 's'}
                    </li>
                  ))}
                </ul>
              </div>
              <UncountedCascade />
            </div>
          ) : (
            <>
              <p className="text-muted">
                This organization holds nothing. Deleting it removes the row and its branding.
              </p>
              <UncountedCascade />
            </>
          )}

          <Field label={`Type “${org.name}” to confirm`}>
            <Input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={org.name}
              autoComplete="off"
              disabled={!org.deletable}
            />
          </Field>
        </div>

        <footer className="flex justify-end gap-2 border-t border-line px-5 py-3.5">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="danger" disabled={!org.deletable || !matches}>
            Delete
          </Button>
        </footer>
      </form>
    </div>
  );
}
